// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { LatLon } from './tileMath';

/**
 * Geometry for a printed-course-guide style drawing of one hole.
 *
 * The output is a schematic, not a map: no imagery, no elevation, just the derived
 * boundaries laid out the way a paper yardage book lays them out — the hole running bottom
 * to top, the green at the head of the page, arcs stepping back down the fairway.
 *
 * Everything here works in metres on a local plane and returns SVG user units in those same
 * metres. That is what makes the arcs honest: a 100 m arc is a circle of radius 100 in the
 * viewBox, so it cannot drift out of scale with the shapes it is drawn over.
 *
 * Pure functions with no React and no SVG imports, so the layout can be tested for what it
 * actually claims — where the front edge is, which arcs are real — without rendering.
 */

const EARTH_RADIUS_M = 6378137;
const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;
const toDegrees = (radians: number): number => (radians * 180) / Math.PI;

/** Breathing room around the drawn geometry, in metres. */
const FRAME_PADDING_M = 18;

/**
 * Arc distances, in metres from the front edge of the green.
 *
 * The set a Swedish course guide prints. Arcs beyond the hole itself are dropped rather
 * than clipped, so a par 3 shows one arc and a long par 5 shows all of them.
 */
export const ARC_DISTANCES_M = [50, 100, 150, 200] as const;

/**
 * How much shorter than the hole an arc has to be to be worth drawing.
 *
 * An arc that lands on the teeing ground is not a lay-up reference, it is a restatement of
 * the hole length, and it collides with the tee markers.
 */
const ARC_CLEARANCE_M = 25;

/** Draw order. Cover goes down first, then hazards, then the green on top of both. */
const KIND_DEPTH: Record<string, number> = {
  trees: 0,
  fairway: 1,
  water: 2,
  bunker: 3,
  green: 4,
};

export type GuideFeature = {
  id: string;
  kind: string;
  ring: LatLon[];
};

export type GuideShape = {
  id: string;
  kind: string;
  /** SVG `points` attribute, in metres. */
  points: string;
};

export type GuidePoint = { x: number; y: number };

export type GuideArc = {
  distanceM: number;
  /** Centre is the green's front edge; the radius is the distance itself. */
  cx: number;
  cy: number;
  r: number;
  /** Where the arc crosses the line of play, which is where the number belongs. */
  labelX: number;
  labelY: number;
};

export type GuideTee = GuidePoint & { label: string | null };

export type HoleGuide = {
  /** `minX minY width height`, in metres. */
  viewBox: string;
  widthM: number;
  heightM: number;
  shapes: GuideShape[];
  arcs: GuideArc[];
  tees: GuideTee[];
  greenCentre: GuidePoint;
  /**
   * The point on the green nearest the tee. Null when no green *polygon* is stored — the
   * centre alone cannot say where the front is, and guessing a front edge from a radius
   * would put a number on the screen that nothing measured.
   */
  greenFront: GuidePoint | null;
  /** Back tee to green centre, for the line of play. */
  playLine: { x1: number; y1: number; x2: number; y2: number };
  /** Back tee to green centre, in metres. */
  holeLengthM: number;
  /** Kinds actually drawn, so the caller can render a legend of what is really there. */
  kinds: string[];
  /**
   * Compass bearing the hole plays along, tee to green — the same convention
   * `computeHoleViewLayout`'s own `holeBearing` takes. A caller framing a *different* view of
   * the same hole and wanting it to turn the same way passes this straight through, rather
   * than recomputing a bearing that could drift out of step with the one this drawing used.
   */
  holeBearingDegrees: number;
  /**
   * Two opposite corners of this drawing's own fitted viewBox, carried back out to real
   * lat/lon. Exists for exactly one reason: letting another view of the same hole — one that
   * works in real coordinates, not this drawing's local metres plane — fit itself to show
   * the identical patch of ground, so two different renderings of one hole end up at the
   * same scale instead of two independently-guessed ones.
   */
  frameCorners: [LatLon, LatLon];
};

type Plane = {
  lat0: number;
  lon0: number;
  sinBearing: number;
  cosBearing: number;
};

/** Metres east and north of the plane's origin. */
const offsetMeters = (point: LatLon, lat0: number, lon0: number): GuidePoint => ({
  x: toRadians(point.lon - lon0) * EARTH_RADIUS_M * Math.cos(toRadians(lat0)),
  y: toRadians(point.lat - lat0) * EARTH_RADIUS_M,
});

/**
 * Projects to SVG metres with the hole running up the page.
 *
 * The plane is rotated so +along is the direction of play, and the sign of y is flipped
 * because SVG counts downwards: the tee sits at y=0 and the green above it at negative y.
 */
const project = (point: LatLon, plane: Plane): GuidePoint => {
  const { x: east, y: north } = offsetMeters(point, plane.lat0, plane.lon0);
  const along = east * plane.sinBearing + north * plane.cosBearing;
  const across = east * plane.cosBearing - north * plane.sinBearing;
  return { x: across, y: -along };
};

/**
 * The inverse of {@link project}: a point in the guide's own metres plane back to a real
 * lat/lon. Used to hand the guide's fitted frame — not just its shapes — to a caller that
 * needs to fit *something else* to the exact same patch of ground, in real coordinates.
 *
 * The projection is a rotation (plus SVG's y flip), and a rotation matrix's inverse is its
 * own transpose — which happens to make this matrix its own inverse, so the maths mirrors
 * `project` almost exactly rather than needing a general matrix solve.
 */
const unproject = (point: GuidePoint, plane: Plane): LatLon => {
  const along = -point.y;
  const across = point.x;
  const east = along * plane.sinBearing + across * plane.cosBearing;
  const north = along * plane.cosBearing - across * plane.sinBearing;
  return {
    lat: plane.lat0 + toDegrees(north / EARTH_RADIUS_M),
    lon: plane.lon0 + toDegrees(east / (EARTH_RADIUS_M * Math.cos(toRadians(plane.lat0)))),
  };
};

const distanceBetween = (a: GuidePoint, b: GuidePoint): number =>
  Math.hypot(a.x - b.x, a.y - b.y);

/**
 * The back tee: the one the hole plays longest from, which is the one the card measures
 * and the one a guide draws from.
 */
export const backTeeOf = (tees: LatLon[], green: LatLon | null): LatLon | null => {
  if (!green || tees.length === 0) return null;
  return tees.reduce((longest, tee) => {
    const a = offsetMeters(tee, green.lat, green.lon);
    const b = offsetMeters(longest, green.lat, green.lon);
    return Math.hypot(a.x, a.y) > Math.hypot(b.x, b.y) ? tee : longest;
  });
};

/**
 * Keeps the features that belong in this hole's picture.
 *
 * Attribution is not enough on its own. The cover classes are cut from the whole course at
 * once — one fairway component can span two holes and a tree belt runs the length of a
 * stretch — so they are attributed to whichever hole their centroid happened to land in,
 * and filtering on hole number alone leaves a neighbouring hole's guide with no fairway at
 * all. Anything whose bounding box reaches into the frame is drawn instead, which is both
 * more forgiving and closer to what the eye expects.
 */
const featuresInFrame = (
  features: GuideFeature[],
  frame: { minX: number; minY: number; maxX: number; maxY: number },
  plane: Plane
): GuideFeature[] =>
  features.filter((feature) => {
    if (feature.ring.length < 3) return false;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const vertex of feature.ring) {
      const point = project(vertex, plane);
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    }
    return maxX >= frame.minX && minX <= frame.maxX && maxY >= frame.minY && minY <= frame.maxY;
  });

export type BuildHoleGuideInput = {
  tees: { lat: number; lon: number; label?: string | null }[];
  /** Green centre. Without it there is no line of play, and so no drawing. */
  green: LatLon | null;
  /** Every polygon on the course; this picks the ones that reach into the hole's frame. */
  features: GuideFeature[];
};

/**
 * Lays out one hole, or returns null when there is not enough geometry to lay out.
 *
 * Null rather than a partial drawing: a guide with no line of play is a scatter of shapes
 * with distances that mean nothing, and the caller has a perfectly good "not mapped yet"
 * message to show instead.
 */
export const buildHoleGuide = ({ tees, green, features }: BuildHoleGuideInput): HoleGuide | null => {
  const backTee = backTeeOf(tees, green);
  if (!green || !backTee) return null;

  const { x: east, y: north } = offsetMeters(green, backTee.lat, backTee.lon);
  const bearing = Math.atan2(east, north);
  const plane: Plane = {
    lat0: backTee.lat,
    lon0: backTee.lon,
    sinBearing: Math.sin(bearing),
    cosBearing: Math.cos(bearing),
  };

  const teePoints: GuideTee[] = tees.map((tee) => ({
    ...project(tee, plane),
    label: tee.label ?? null,
  }));
  const greenCentre = project(green, plane);

  // The frame the cover classes are clipped against: the corridor from the tee to the
  // green, widened enough to take in the rough and any greenside trouble either side.
  const corridor = {
    minX: -70,
    maxX: 70,
    minY: Math.min(greenCentre.y, 0) - 60,
    maxY: Math.max(greenCentre.y, 0) + 40,
  };

  const drawn = featuresInFrame(features, corridor, plane)
    .map((feature) => ({
      id: feature.id,
      kind: feature.kind,
      projected: feature.ring.map((vertex) => project(vertex, plane)),
    }))
    // Stable within a kind so a re-render never reshuffles overlapping shapes.
    .sort((a, b) => (KIND_DEPTH[a.kind] ?? 9) - (KIND_DEPTH[b.kind] ?? 9));

  const shapes: GuideShape[] = drawn.map((feature) => ({
    id: feature.id,
    kind: feature.kind,
    points: feature.projected.map((point) => `${round(point.x)},${round(point.y)}`).join(' '),
  }));

  // The green to measure to is the one around the green *centre*, not just any green in
  // frame: on a tight routing the next hole's green reaches into the corridor, and taking
  // the wrong one would move every arc on the page.
  const greenRing =
    drawn
      .filter((feature) => feature.kind === 'green')
      .map((feature) => feature.projected)
      .sort(
        (a, b) => distanceBetween(centroidOf(a), greenCentre) - distanceBetween(centroidOf(b), greenCentre)
      )[0] ?? null;

  // The front edge is the vertex of the green nearest the tee. y grows towards the tee once
  // the plane is rotated, so that is simply the largest y on the ring.
  const greenFront = greenRing
    ? roundPoint(greenRing.reduce((nearest, point) => (point.y > nearest.y ? point : nearest)))
    : null;

  const holeLengthM = distanceBetween({ x: 0, y: 0 }, greenCentre);

  const arcs: GuideArc[] = greenFront
    ? ARC_DISTANCES_M.filter(
        (distance) => distance <= distanceBetween({ x: 0, y: 0 }, greenFront) - ARC_CLEARANCE_M
      ).map((distance) => ({
        distanceM: distance,
        cx: greenFront.x,
        cy: greenFront.y,
        r: distance,
        // Straight back down the line of play from the front edge, which is where the arc
        // crosses the centre of the fairway and where a guide prints the number.
        labelX: greenFront.x,
        labelY: round(greenFront.y + distance),
      }))
    : [];

  // Every point that has to stay inside the picture. The arcs are included by their
  // crossing point rather than their full circle: an arc is meant to run off both edges.
  const bounds = boundsOf([
    { x: 0, y: 0 },
    greenCentre,
    ...teePoints,
    ...drawn.flatMap((feature) => feature.projected),
    ...arcs.map((arc) => ({ x: arc.labelX, y: arc.labelY })),
  ]);

  const minX = bounds.minX - FRAME_PADDING_M;
  const minY = bounds.minY - FRAME_PADDING_M;
  const widthM = bounds.maxX - bounds.minX + FRAME_PADDING_M * 2;
  const heightM = bounds.maxY - bounds.minY + FRAME_PADDING_M * 2;

  return {
    viewBox: `${round(minX)} ${round(minY)} ${round(widthM)} ${round(heightM)}`,
    widthM,
    heightM,
    shapes,
    arcs,
    tees: teePoints,
    greenCentre,
    greenFront,
    playLine: { x1: 0, y1: 0, x2: round(greenCentre.x), y2: round(greenCentre.y) },
    holeLengthM: Math.round(holeLengthM),
    kinds: [...new Set(shapes.map((shape) => shape.kind))],
    holeBearingDegrees: toDegrees(bearing),
    frameCorners: [
      unproject({ x: minX, y: minY }, plane),
      unproject({ x: minX + widthM, y: minY + heightM }, plane),
    ],
  };
};

const centroidOf = (points: GuidePoint[]): GuidePoint => ({
  x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
  y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
});

const boundsOf = (points: GuidePoint[]) => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  return { minX, minY, maxX, maxY };
};

/** Centimetre precision. Anything finer is noise in a drawing scaled to a phone screen. */
const round = (value: number): number => Math.round(value * 100) / 100;

const roundPoint = (point: GuidePoint): GuidePoint => ({ x: round(point.x), y: round(point.y) });
